import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalculator } from '@fortawesome/free-solid-svg-icons';
import './Calculator.css';

const Calculator = ({ isOpen, onClose }) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [memory, setMemory] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const calculatorRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDisplay('0');
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(false);
      // Focus the calculator when opened
      setTimeout(() => {
        if (calculatorRef.current) {
          calculatorRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Focus the calculator when opened
      setTimeout(() => {
        if (calculatorRef.current) {
          calculatorRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.calculator-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart.x, dragStart.y]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const inputNumber = (num) => {
    if (waitingForOperand) {
      setDisplay(String(num));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(num) : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '*':
        return firstValue * secondValue;
      case '/':
        return secondValue !== 0 ? firstValue / secondValue : 'Error';
      default:
        return secondValue;
    }
  };

  const performEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const result = calculate(previousValue, inputValue, operation);
      setDisplay(String(result));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const performSpecialOperation = (operation) => {
    const inputValue = parseFloat(display);

    switch (operation) {
      case 'sqrt':
        if (inputValue >= 0) {
          setDisplay(String(Math.sqrt(inputValue)));
        } else {
          setDisplay('Error');
        }
        break;
      case '1/x':
        if (inputValue !== 0) {
          setDisplay(String(1 / inputValue));
        } else {
          setDisplay('Error');
        }
        break;
      case '+/-':
        setDisplay(String(-inputValue));
        break;
      default:
        break;
    }
    setWaitingForOperand(true);
  };

  const memoryOperation = (operation) => {
    const inputValue = parseFloat(display);

    switch (operation) {
      case 'MC':
        setMemory(0);
        break;
      case 'MR':
        setDisplay(String(memory));
        if (previousValue !== null && operation) {
          // If there's a pending operation, we're now entering the second operand
          setWaitingForOperand(false);
        } else {
          // Otherwise, start fresh
          setPreviousValue(null);
          setOperation(null);
          setWaitingForOperand(false);
        }
        break;
      case 'M+':
        setMemory(memory + inputValue);
        setWaitingForOperand(true);
        break;
      default:
        break;
    }
  };

  // Keyboard event handler
  const handleKeyPress = useCallback((e) => {
    if (!isOpen) return;

    const key = e.key;

    // Prevent default behavior for calculator keys
    if ('0123456789.+-*/=cCmMrRs'.includes(key) || key === 'Enter' || key === 'Backspace' || key === 'Delete' || key === 'Escape') {
      e.preventDefault();
    }

    // Number keys
    if (key >= '0' && key <= '9') {
      inputNumber(key);
    }
    // Decimal point
    else if (key === '.') {
      inputDecimal();
    }
    // Operations
    else if (key === '+') {
      performOperation('+');
    }
    else if (key === '-') {
      performOperation('-');
    }
    else if (key === '*') {
      performOperation('*');
    }
    else if (key === '/') {
      performOperation('/');
    }
    // Equals (Enter key)
    else if (key === 'Enter' || key === '=') {
      performEquals();
    }
    // Clear (C or c)
    else if (key === 'c' || key === 'C') {
      clear();
    }
    // Memory operations
    else if (key === 'm' || key === 'M') {
      memoryOperation('M+');
    }
    else if (key === 'r' || key === 'R') {
      memoryOperation('MR');
    }
    else if (key === 's' || key === 'S') {
      memoryOperation('MC');
    }
    // Backspace/Delete for clearing last digit
    else if (key === 'Backspace' || key === 'Delete') {
      if (display !== '0' && display.length > 1) {
        setDisplay(display.slice(0, -1));
      } else {
        setDisplay('0');
      }
    }
    // Escape to close calculator
    else if (key === 'Escape') {
      onClose();
    }
  }, [isOpen, display, onClose, inputNumber, inputDecimal, performOperation, performEquals, clear, memoryOperation]);

  if (!isOpen) return null;

  return (
    <div className="calculator-overlay">
      <div
        ref={calculatorRef}
        className="calculator-modal"
        style={{
          left: position.x,
          top: position.y,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
        tabIndex={-1}
        onKeyDown={handleKeyPress}
      >
        <div className="calculator-header">
          <FontAwesomeIcon icon={faCalculator} className="calculator-icon" />
          <span className="calculator-title">Calculator</span>
          <button className="calculator-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="calculator-display">
          <div className="calculator-memory-indicator">
            {memory !== 0 && 'M'}
          </div>
          <div className="calculator-display-value">
            {previousValue !== null && operation 
              ? waitingForOperand 
                ? `${previousValue}${operation}` 
                : `${previousValue}${operation}${display}`
              : display}
          </div>
        </div>

        <div className="calculator-buttons">
          <div className="calculator-row">
            <button className="calculator-btn memory-btn" onClick={() => memoryOperation('MC')}>MC</button>
            <button className="calculator-btn memory-btn" onClick={() => memoryOperation('MR')}>MR</button>
            <button className="calculator-btn memory-btn" onClick={() => memoryOperation('M+')}>M+</button>
            <button className="calculator-btn operation-btn" onClick={clear}>C</button>
          </div>

          <div className="calculator-row">
            <button className="calculator-btn special-btn" onClick={() => performSpecialOperation('sqrt')}>√</button>
            <button className="calculator-btn special-btn" onClick={() => performSpecialOperation('+/-')}>±</button>
            <button className="calculator-btn special-btn" onClick={() => performSpecialOperation('1/x')}>1/x</button>
            <button className="calculator-btn operation-btn" onClick={() => performOperation('/')}>÷</button>
          </div>

          <div className="calculator-row">
            <button className="calculator-btn number-btn" onClick={() => inputNumber(7)}>7</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(8)}>8</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(9)}>9</button>
            <button className="calculator-btn operation-btn" onClick={() => performOperation('*')}>×</button>
          </div>

          <div className="calculator-row">
            <button className="calculator-btn number-btn" onClick={() => inputNumber(4)}>4</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(5)}>5</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(6)}>6</button>
            <button className="calculator-btn operation-btn" onClick={() => performOperation('-')}>-</button>
          </div>

          <div className="calculator-row">
            <button className="calculator-btn number-btn" onClick={() => inputNumber(1)}>1</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(2)}>2</button>
            <button className="calculator-btn number-btn" onClick={() => inputNumber(3)}>3</button>
            <button className="calculator-btn operation-btn" onClick={() => performOperation('+')}>+</button>
          </div>

          <div className="calculator-row">
            <button className="calculator-btn number-btn zero-btn" onClick={() => inputNumber(0)}>0</button>
            <button className="calculator-btn number-btn" onClick={inputDecimal}>.</button>
            <button className="calculator-btn equals-btn" onClick={performEquals}>=</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;